/**
 * analytics-shared/api/metadataApi.js
 *
 * Tenant-aware object & field catalog for the reports module.
 *
 * Wire-up:
 *   GET /api/objects                       — list of TenantObject with embedded
 *                                            fields dict (lookup_object included
 *                                            inline for lookup-typed fields).
 *                                            Gated by sales_console (every CRM
 *                                            user, not admin-only).
 *   GET /api/objects/{name}/all-fields     — richer per-field array including
 *                                            standard + custom + advanced +
 *                                            system fields with field_type
 *                                            classification.
 *
 * Both come from backend/modules/metadata/api/metadata_routes.py.
 *
 * The single /api/objects call returns enough information to derive the
 * entire Salesforce-style report-type catalog client-side (primary objects +
 * their lookup-driven related objects). We only fall back to /all-fields
 * when a consumer needs the richer custom/advanced/system field set, e.g.
 * the in-builder Fields panel.
 *
 * Fixture mode (REACT_APP_USE_REPORT_FIXTURES=true) is preserved for offline
 * demos / storybook only.
 */
import {
  REPORT_OBJECTS,
  getObject as getFixtureObject, getFieldMap as getFixtureFieldMap,
  getField as getFixtureField,
} from '../fixtures/reportObjects';
import { OBJECT_CATEGORIES } from '../constants';
import { AGGREGATIONS, OPERATORS, DATE_PRESETS } from '../engine/reportEngine';
import { crmClient, analyticsClient, unwrap } from './client';

const USE_FIXTURES = process.env.REACT_APP_USE_REPORT_FIXTURES === 'true';

// ──────────────────────────────────────────────────────────────────────────
// Normalisers
// ──────────────────────────────────────────────────────────────────────────

// Backend field types map onto the engine's internal taxonomy (text / number /
// currency / percent / date / select / user). Anything unrecognised falls
// through to 'text', which keeps the filter UI usable.
const FIELD_TYPE_MAP = {
  text: 'text',
  string: 'text',
  long_text: 'text',
  textarea: 'text',
  email: 'text',
  phone: 'text',
  url: 'text',
  number: 'number',
  integer: 'number',
  decimal: 'number',
  currency: 'currency',
  money: 'currency',
  percent: 'percent',
  percentage: 'percent',
  date: 'date',
  datetime: 'date',
  timestamp: 'date',
  select: 'select',
  picklist: 'select',
  multipicklist: 'select',
  boolean: 'select',
  checkbox: 'select',
  user: 'user',
  owner: 'user',
  lookup: 'text',
  reference: 'text',
};

const normaliseFieldType = (t) => FIELD_TYPE_MAP[(t || '').toLowerCase()] || 'text';

// Bucket fields into the four UI sections (standard / custom / formula /
// related). Honours an explicit field_type from /all-fields when present,
// then falls back to heuristics.
const deriveFieldCategory = (raw) => {
  const explicit = raw.field_type || raw.fieldCategory;
  if (explicit === 'system') return 'standard';
  if (explicit === 'custom') return raw.is_computed ? 'formula' : 'custom';
  if (explicit === 'standard') return 'standard';
  if (raw.is_custom) return raw.is_computed ? 'formula' : 'custom';
  const apiName = raw.api_name || raw.name || raw.key || '';
  if (apiName.endsWith('__c')) return 'custom';
  if (raw.type === 'formula' || raw.type === 'rollup') return 'formula';
  if (raw.type === 'lookup' || raw.type === 'reference') return 'related';
  return 'standard';
};

const normaliseField = (raw) => {
  const key = raw.api_name || raw.name || raw.key;
  return {
    key,
    label: raw.label || key,
    type: normaliseFieldType(raw.type),
    rawType: (raw.type || '').toLowerCase(),
    fieldCategory: deriveFieldCategory(raw),
    required: !!raw.required,
    values: raw.picklist_values || raw.options || undefined,
    lookupObject: raw.lookup_object || undefined,
    isSystem: !!raw.is_system,
    isCustom: !!raw.is_custom,
    sortOrder: raw.sort_order,
  };
};

// /api/objects embeds field defs as `fields: { api_name: field_def }`. Flatten
// into the normalised array shape the rest of the app expects.
const fieldsFromEmbeddedDict = (dict) => {
  if (!dict || typeof dict !== 'object') return [];
  return Object.entries(dict).map(([api_name, def]) => normaliseField({
    api_name,
    ...def,
  }));
};

const normaliseObject = (raw) => {
  const key = raw.object_name || raw.name || raw.api_name;
  const fields = Array.isArray(raw.fields)
    ? raw.fields.map(normaliseField)
    : fieldsFromEmbeddedDict(raw.fields);

  // Pick a sensible "Name" column: prefer common conventions, else first text.
  const conventionalName = ['name', `${key}_name`, 'subject', 'title']
    .find((c) => fields.some((f) => f.key === c));
  const firstText = fields.find((f) => f.type === 'text');
  const nameField = conventionalName || (firstText && firstText.key) || null;

  return {
    key,
    label: raw.object_label || raw.label || key,
    plural: raw.object_plural || raw.plural_label || raw.label,
    category: raw.category || (raw.is_custom ? 'Custom' : 'CRM'),
    description: raw.description || '',
    icon: raw.icon || undefined,
    isCustom: !!raw.is_custom,
    isFromSchemaBuilder: !!raw.is_from_schema_builder,
    fields,
    nameField,
    recentlyUsed: false,
  };
};

// ──────────────────────────────────────────────────────────────────────────
// Live API
// ──────────────────────────────────────────────────────────────────────────

async function liveListObjects() {
  const raw = await crmClient.get('/objects').then(unwrap);
  return (raw || []).map(normaliseObject);
}

async function liveGetObject(key) {
  if (!key) return null;
  // /all-fields gives the richer per-field metadata (custom + advanced +
  // system). If the user is just browsing the picker, the embedded fields
  // from listObjects are usually enough — but the in-builder field panel
  // wants the full set, so we hit /all-fields here.
  try {
    const richFields = await crmClient
      .get(`/objects/${encodeURIComponent(key)}/all-fields`)
      .then(unwrap);

    // Pair the rich field list with the object's top-level info from the
    // list endpoint. Fetched in parallel via cached list call.
    const list = await crmClient.get('/objects').then(unwrap);
    const row = (list || []).find((o) => (o.object_name || o.name) === key);
    if (!row) return null;
    const merged = normaliseObject({ ...row, fields: richFields?.fields || [] });
    return merged;
  } catch (e) {
    // /all-fields can 404 for objects without a record def. Fall back to the
    // embedded fields from /objects so the UI still has something to render.
    const list = await crmClient.get('/objects').then(unwrap);
    const row = (list || []).find((o) => (o.object_name || o.name) === key);
    return row ? normaliseObject(row) : null;
  }
}

async function liveGetFieldMap(key) {
  const obj = await liveGetObject(key);
  if (!obj || !obj.fields) return {};
  return obj.fields.reduce((acc, f) => { acc[f.key] = f; return acc; }, {});
}

async function liveGetField(objectKey, fieldKey) {
  const map = await liveGetFieldMap(objectKey);
  return map[fieldKey] || null;
}

// ──────────────────────────────────────────────────────────────────────────
// Public surface
// ──────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────
// Report-type catalog — backend-driven after the relationship wedge.
//
// The server's /v1/report-types endpoint walks tenant_objects + their
// lookup fields and emits the same shape `deriveReportTypes` produces
// client-side, plus optional 3-way "Accounts with Leads and Contacts"
// combinations. Going through the backend ensures every consumer sees
// the same catalog regardless of how many objects have synced into the
// browser's metadata cache.
//
// Field catalog is POST (not GET) because the request body carries the
// full reportType blob — the type id alone isn't enough to reconstruct
// the related[] list on the server side without round-tripping through
// the catalog cache.
// ──────────────────────────────────────────────────────────────────────────

async function liveListReportTypes({ primaryObject, includeMulti = true } = {}) {
  const params = {};
  if (primaryObject) params.primary_object = primaryObject;
  if (includeMulti === false) params.include_multi = false;
  return analyticsClient
    .get('/report-types', { params })
    .then(unwrap);
}

async function liveGetReportGraph() {
  try {
    return await analyticsClient.get('/report-graph').then(unwrap);
  } catch (err) {
    // Backend without the guided-builder endpoint yet (staged rollout) —
    // synthesize the graph from the legacy report-type catalog so the
    // create flow keeps working: single types → objects, binary cross
    // types → relations keyed by primary object.
    if (err?.status !== 404) throw err;
    const types = await liveListReportTypes({ includeMulti: false });
    const objects = [];
    const relations = {};
    (types || []).forEach((t) => {
      if (t.kind === 'single' && t.primary?.key) {
        objects.push({
          key: t.primary.key,
          label: t.primary.label,
          plural: t.primary.plural || t.primary.label,
          is_custom: !!t.isCustom,
          field_count: t.fieldCount || 0,
          category: t.category,
        });
      } else if (t.kind === 'cross' && t.primary?.key && (t.related || []).length === 1) {
        const rel = t.related[0];
        const list = relations[t.primary.key] || (relations[t.primary.key] = []);
        list.push({ ...rel, ambiguous: false });
      }
    });
    // Flag duplicate targets so the picker still disambiguates by via.
    Object.values(relations).forEach((rels) => {
      const counts = {};
      rels.forEach((r) => { counts[r.key] = (counts[r.key] || 0) + 1; });
      rels.forEach((r) => { r.ambiguous = counts[r.key] > 1; });
    });
    return { objects, relations };
  }
}

// Typeahead search over an object's records — powers lookup-field filter
// values ("Account is any of …"). Returns [{id, label}].
const NAME_KEYS = ['name', 'account_name', 'full_name', 'subject', 'title', 'label', 'email'];
const recordLabel = (rec) => {
  const d = rec?.data && typeof rec.data === 'object' ? rec.data : rec || {};
  const first = d.first_name || '';
  const last = d.last_name || '';
  if (first || last) return `${first} ${last}`.trim();
  for (const k of NAME_KEYS) {
    if (d[k]) return String(d[k]);
  }
  return rec?.id || 'Unnamed';
};

async function liveSearchRecords(objectKey, query, limit = 10) {
  const res = await crmClient
    .get(`/objects/${encodeURIComponent(objectKey)}/records`, {
      params: { search: query || undefined, limit, page: 1 },
    })
    .then(unwrap);
  const records = Array.isArray(res) ? res : res?.records || [];
  return records.map((r) => ({ id: r.id, label: recordLabel(r) }));
}

async function liveListUsers(query) {
  const users = await crmClient.get('/users').then(unwrap);
  const q = (query || '').trim().toLowerCase();
  return (Array.isArray(users) ? users : [])
    .map((u) => ({
      id: u.id,
      label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id,
    }))
    .filter((u) => !q || u.label.toLowerCase().includes(q));
}

async function liveValidateFilterLogic(expression, conditionCount) {
  return analyticsClient
    .post('/filter-logic/validate', { expression, conditionCount })
    .then(unwrap);
}

async function liveGetReportTypeFields(reportType) {
  if (!reportType || !reportType.primary?.key) {
    return { primary: null, groups: [], field_map: {} };
  }
  return analyticsClient
    .post('/report-types/fields', {
      primary: reportType.primary,
      related: reportType.related || [],
    })
    .then(unwrap);
}

export const metadataApi = {
  listObjects: async () => (USE_FIXTURES ? REPORT_OBJECTS : liveListObjects()),
  getObject: async (key) => (USE_FIXTURES ? getFixtureObject(key) : liveGetObject(key)),
  getFieldMap: async (key) => (USE_FIXTURES ? getFixtureFieldMap(key) : liveGetFieldMap(key)),
  getField: async (objectKey, fieldKey) => (
    USE_FIXTURES ? getFixtureField(objectKey, fieldKey) : liveGetField(objectKey, fieldKey)
  ),
  // Backend-derived report-type catalog. Returns the full list; callers
  // can filter/group locally via `filterReportTypes` from the JS engine
  // (kept for backward compatibility — same shape, same field names).
  listReportTypes: async (opts) => (USE_FIXTURES ? [] : liveListReportTypes(opts)),
  // Object graph for the guided builder — objects + available relations
  // per object. Drives "choose a primary, then add related objects".
  getReportGraph: async () => (
    USE_FIXTURES ? { objects: [], relations: {} } : liveGetReportGraph()
  ),
  // Lookup-filter typeahead: records of `objectKey` matching `query`.
  searchRecords: async (objectKey, query, limit) => (
    USE_FIXTURES ? [] : liveSearchRecords(objectKey, query, limit)
  ),
  // User picker for owner/user-field filters (client-side filtered).
  listUsers: async (query) => (USE_FIXTURES ? [] : liveListUsers(query)),
  // Live validation for the custom filter-logic expression editor.
  validateFilterLogic: async (expression, conditionCount) => (
    USE_FIXTURES ? { valid: true, error: null } : liveValidateFilterLogic(expression, conditionCount)
  ),
  // Field catalog for a given report type — the builder calls this on
  // mount to populate the per-object "Account Fields / Lead Fields /
  // Contact Fields" sidebar sections.
  getReportTypeFields: async (reportType) => (
    USE_FIXTURES ? { primary: reportType?.primary || null, groups: [], field_map: {} }
      : liveGetReportTypeFields(reportType)
  ),
  listAggregations: async () => AGGREGATIONS,
  listOperators: async () => OPERATORS,
  listDatePresets: async () => DATE_PRESETS,
  listCategories: async () => OBJECT_CATEGORIES,
};
